import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubResponse,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * Arguments for {@link buildBulkAnswerUpdate}. Bundled in a single
 * object so future fields (per-child comment / naReason overrides,
 * `expectedLastUpdatedAt`) slot in without shifting args.
 */
export interface BuildBulkAnswerUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — recorded as `respondedBy` / `lastUpdatedBy`
   *  on the outgoing payload. Stamped identically on every child. */
  readonly alias: string;
  /** Pass / Fail / N/A applied uniformly to every sub-question. */
  readonly result: ResponseResult;
  /**
   * IDs of the sub-questions to overwrite. The bulk builder writes
   * an entry per id with the tapped result. Consumers pass the ids
   * of EVERY sub-question defined on the parent — omitting an id
   * would leave that child's server-side answer untouched, which
   * defeats "Pass All" semantics.
   *
   * Typed as a plain string array (not full `SubQuestion` shapes)
   * because that's all the wire payload needs; the caller extracts
   * ids once from `DisplayableSubQuestion[]` at the boundary.
   */
  readonly subQuestionIds: readonly string[];
  /**
   * The parent question's cached response, or undefined when the
   * parent has never been answered. Preservation is the same as
   * {@link buildAnswerUpdate}: comment / naReason / mediaIds /
   * taskIds survive the round-trip. Sub-answers on OTHER children
   * are wiped by design — the bulk write is an overwrite, not a
   * merge.
   */
  readonly previous?: QuestionResponseDetails | undefined;
}

/**
 * Builds the mutation input for a BULK Pass All / Fail All / N/A All
 * save. Every sub-question on the parent receives the same tapped
 * result; the parent's aggregate result is set to that same value
 * (uniform children → parent's derivation collapses to the shared
 * result, which we compute client-side rather than round-tripping
 * server derivation).
 *
 * Contract vs the two sibling builders:
 *
 *   - vs {@link buildAnswerUpdate} (parent-flat path): both set the
 *     top-level `responseResult`. Difference is `subResponses` —
 *     parent-flat preserves any existing sub-answers verbatim, bulk
 *     OVERWRITES every child.
 *   - vs {@link buildSubQuestionAnswerUpdate} (per-row path): bulk
 *     sets top-level `responseResult` because the children ARE
 *     uniform (client-side certainty); per-row leaves it unset
 *     because the server has to derive from a mix.
 *
 * Pure — no react, no side effects. Consumed by the bulk draft
 * hook and by the bulk optimistic cache patch (both must write the
 * same shape so wire and cache stay coherent).
 *
 * standards-ignore: DRY:buildPreservedResponse — the parent-field
 *   preservation helper duplicates the copy in `buildAnswerUpdate`
 *   and `buildSubQuestionAnswerUpdate`. Three copies is the point
 *   at which extraction pays off; scheduled for the CR-V6 slice
 *   when the same shape lands on the comment-save builder and a
 *   fourth copy would otherwise appear.
 */
export function buildBulkAnswerUpdate(
  args: BuildBulkAnswerUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const responseStatus =
    args.result === ResponseResult.NA ? ResponseStatus.NA : ResponseStatus.COMPLETED;

  const subResponses: SubResponse[] = args.subQuestionIds.map((subQuestionId) => ({
    subQuestionId,
    result: args.result,
  }));

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus,
    responseResult: args.result,
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
    subResponses,
  };

  const preservedResponse = buildPreservedResponse(args.previous, {
    clearNaReason: args.result !== ResponseResult.NA,
  });
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  return input;
}

/**
 * Copy of the parent-path preserved-response builder. See
 * `buildAnswerUpdate.buildPreservedResponse` docblock for the
 * design contract — we mirror it verbatim to keep all three
 * mutation paths bitwise-compatible for the parent's comment /
 * naReason / mediaIds / taskIds fields. See top-of-file
 * standards-ignore for the extraction note.
 */
function buildPreservedResponse(
  previous: QuestionResponseDetails | undefined,
  options: { readonly clearNaReason: boolean } = { clearNaReason: false },
): UpdateAuditQuestionResponseCommandInput['response'] | undefined {
  if (!previous) return undefined;
  const hasComment =
    previous.responseComment !== undefined && previous.responseComment !== '';
  // Clear the parent's naReason on the wire when the bulk result
  // isn't NA — a previously-picked reason must not survive a
  // Pass All / Fail All / etc. answer change. When `clearNaReason`
  // is true, drop the field regardless of what previous holds.
  const hasNaReason =
    !options.clearNaReason && previous.naReason !== undefined && previous.naReason !== '';
  const hasMediaIds = !!previous.mediaIds && previous.mediaIds.length > 0;
  const hasTaskIds = !!previous.taskIds && previous.taskIds.length > 0;
  if (!hasComment && !hasNaReason && !hasMediaIds && !hasTaskIds) {
    return undefined;
  }
  const response: UpdateAuditQuestionResponseCommandInput['response'] = {};
  if (hasComment) response.comment = previous.responseComment;
  if (hasNaReason) response.naReason = previous.naReason;
  if (hasMediaIds) response.mediaIds = previous.mediaIds;
  if (hasTaskIds) response.taskIds = previous.taskIds;
  return response;
}
